import React from 'react';

import { TOOL_IDS } from '@adsk/alloy-provider';
import { SheetsIcon } from '@adsk/alloy-react-icon';
import { useLocale } from '@adsk/alloy-react-locale';
import { TabTitle } from '@adsk/exo-skeleton-react';

import { GET_SHEETS_RESPONSE_PLACEHOLDER, withProps } from './helpers/consts';
import Services from './helpers/sdkServices';
import {
  chunk,
  formatSheetHistory,
  formatSheetVersion,
  getProductId,
} from './helpers/utils';
import Provider from './providers';
import ActivityCard from './SheetActivityCard';
import CustomDataView from './SheetCustomDataView';
import CustomFilterButton from './SheetCustomFilterButton';
import CustomPickerSearchBox from './SheetCustomPickerSearchBox';
import EntityBlock from './SheetEntityBlock';
import EntityMenuItem from './SheetEntityMenuItem';
import FiltersPanel from './SheetFiltersPanel';

const defaultSheetContainerType = 'Build';

const sheetPluginsCommon = (sheetParser, sheetContainerType) => ({
  renderIcon: (p) => <SheetsIcon {...p} />,
  productId: getProductId(sheetContainerType),
  toolId: TOOL_IDS.SHEETS,
  showSearchBox: true,
  is3LOSupported: true,
  modalProps: {
    width: 1140,
    height: 700,
  },
  PluralText: ({ lowerCase }) => {
    const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
    const result = T.translate('SHEET');
    if (lowerCase && typeof result === 'string') {
      return result.toLowerCase();
    }
    return result;
  },
  PickerModalTitle: withProps(({ showTabTitle = true }) => {
    const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
    const value = T.translate('ADD_SHEET');

    return (
      <>
        {showTabTitle && <TabTitle value={value} />}
        {value}
      </>
    );
  }, ''),
  AddRelationshipsButtonText: ({ count }) => {
    const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
    return T.translate(
      `ADD_${count > 1 ? 'MULTI_' : count === 1 ? 'ONE_' : ''}BUTTON`,
    );
  },
  SelectedCount: ({ count }) => {
    const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
    return T.translate(
      `${count > 1 ? 'MULTI_' : count === 1 ? 'ONE_' : ''}SELECTED_COUNT`,
      { context: count },
    );
  },
  // A workaround for collection, queryEntities only works as an entry
  // SheetCustomDataView handle queryEntities logic
  // Keep async here, framework requires thenable return
  queryEntities: async () => ({ data: GET_SHEETS_RESPONSE_PLACEHOLDER }),
  EntityBlock,
  CustomDataView,
  CustomPickerSearchBox,
  CustomFilterButton,
  FiltersPanel,
  EntityMenuItem,
  ActivityCard,
  // eslint-disable-next-line
  Provider: ({ children }) => {
    return (
      <Provider
        sheetContainerType={sheetContainerType}
        sheetParser={sheetParser}
      >
        {children}
      </Provider>
    );
  },
});

const sheetLineageCommon = (sheetContainerType) => {
  return {
    ...sheetPluginsCommon(formatSheetHistory, sheetContainerType),
    metadata: {
      domain: 'autodesk-construction-sheet',
      type: 'sheetlineage',
    },
    getEntitiesByIds: async ({ ids, projectId }) => {
      let data = [];
      await chunk(ids, 50).reduce((pre, resources) => {
        return pre.then(() =>
          Services.Sheets.getSheetsHistories(
            projectId,
            {
              ids: resources,
              versionLimit: 1,
              collectionId: '*',
            },
            { sheetContainerType },
          ).then((res) => {
            const versions = res.flatMap(({ versions }) =>
              versions.map(formatSheetHistory),
            );
            data = [...data, ...versions];
          }),
        );
      }, Promise.resolve());
      return { data };
    },
  };
};

const SheetLineage = {
  ...sheetLineageCommon(defaultSheetContainerType),
};

const sheetVersionCommon = (sheetContainerType) => {
  return {
    ...sheetPluginsCommon(formatSheetVersion, sheetContainerType),
    metadata: {
      domain: 'autodesk-construction-sheet',
      type: 'sheetversion',
    },
    getEntitiesByIds: async ({ ids, projectId }) => {
      let data = [];
      await chunk(ids, 200).reduce((pre, resources) => {
        return pre.then(() =>
          Services.Sheets.getSheetsByIds(
            projectId,
            { ids: resources },
            { sheetContainerType },
          ).then((result) => {
            const sheets = result.map(formatSheetVersion);
            data = [...data, ...sheets];
          }),
        );
      }, Promise.resolve());
      return { data };
    },
  };
};

export const sheetVersion = {
  ...sheetVersionCommon(defaultSheetContainerType),
};

/**
 * A function to create sheet version plugin with configs
 *
 * @param {function} onEntityBlockClick - a customized click handler for clicking on EntityBlock, optional, shape (entity) => *
 * @param {function} onEntityMenuItemClick - a customized click handler for clicking on EntityMenuItem, optional, shape (entity) => *
 * @param sheetContainerType - the sheet container type to use (ie. Build, QTO, etc) defaults to 'Build'
 */
export const createVersionPlugin = ({
  onEntityBlockClick,
  onEntityMenuItemClick,
  sheetContainerType = defaultSheetContainerType,
}) => ({
  ...sheetVersionCommon(sheetContainerType),
  EntityBlock: (props) => (
    <EntityBlock {...props} onClick={onEntityBlockClick} />
  ),
  EntityMenuItem: (props) => (
    <EntityMenuItem {...props} onClick={onEntityMenuItemClick} />
  ),
});

/**
 * A function to create sheet lineage plugin with configs
 *
 * @param {function} onEntityBlockClick - a customized click handler for clicking on EntityBlock, optional, shape (entity) => *
 * @param {function} onEntityMenuItemClick - a customized click handler for clicking on EntityMenuItem, optional, shape (entity) => *
 * @param sheetContainerType - the sheet container type to use (ie. Build, QTO, etc) defaults to 'Build'
 */
export const createLineagePlugin = ({
  onEntityBlockClick,
  onEntityMenuItemClick,
  sheetContainerType = defaultSheetContainerType,
}) => ({
  ...sheetLineageCommon(sheetContainerType),
  EntityBlock: (props) => (
    <EntityBlock {...props} onClick={onEntityBlockClick} />
  ),
  EntityMenuItem: (props) => (
    <EntityMenuItem {...props} onClick={onEntityMenuItemClick} />
  ),
});

export default SheetLineage;
